/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { docViewModes } from "constants/enums";
import { getViewMode, getIsInPlaceMode } from "selectors/viewModeSelectors";
import {
  isPageByPageViewMode,
  isContinuousViewMode,
  isSinglePageMode,
  isTwoColumnViewMode,
  getTotalPages
} from "selectors/documentSelectors";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import { getCurrRHPFeature } from "selectors/appSelectors";
import { isReadAloudActive } from "selectors/readAloudSelectors";
import { getPageViewModifier } from "selectors/zoom";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getPageHeight, getPageOrientation } from "selectors/pageSelectors";
import { trackEntryInFullScreenMode, trackExitFromFullScreenMode } from "analytics/events";
import {
  getReasonOfExitFromFullScreenMode,
  getActiveMegaVerbBeforeFullScreenMode,
  getTotalPageVisitedInFullScreenMode
} from "selectors/pageByPageSelectors";
import { getViewPortHeight } from "selectors/documentViewSelectors";

export const getDocViewMode = stores => {
  if (isPageByPageViewMode(stores)) return docViewModes.PAGE_BY_PAGE;
  if (isContinuousViewMode(stores)) return docViewModes.CONTINUOUS;
  if (isSinglePageMode(stores)) return docViewModes.SINGLE_PAGE;
  if (getIsInPlaceMode(stores)) return docViewModes.IN_PLACE;
  if (isTwoColumnViewMode(stores)) return docViewModes.TWO_COLUMN;

  return getViewMode(stores);
};

export const exitFullScreenView = (stores, reason) => {
  stores.pageByPageStore.setFullScreenExitReason(reason);
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitExitFullscreen) {
    /* Safari */
    document.webkitExitFullscreen();
  } else if (document.msExitFullscreen) {
    /* IE11 */
    document.msExitFullscreen();
  }
};

export const trackFullScreenEntryAnalytics = stores => {
  const index = getCurrentPageIndex(stores);
  const fullScreenEntryData = {
    activeMegaVerb: getActiveMegaVerbBeforeFullScreenMode(stores),
    activeQuickToolVerb: getActiveVerb(stores),
    activeRHPVerb: getCurrRHPFeature(stores),
    isReadAloudActive: isReadAloudActive(stores),
    viewMode: getDocViewMode(stores),
    pageViewModifier: getPageViewModifier(stores),
    orientation: getPageOrientation(stores, index)
  };
  trackEntryInFullScreenMode(fullScreenEntryData);
};

export const trackFullScreenExitAnalytics = (stores, timeSpent) => {
  const fullScreenExitData = {
    reasonOfExit: getReasonOfExitFromFullScreenMode(stores),
    numPagesVisited: getTotalPageVisitedInFullScreenMode(stores),
    totalPages: getTotalPages(stores),
    timeSpent
  };
  trackExitFromFullScreenMode(fullScreenExitData);
};

export const getCurrentPageIndexUtilForFitOnePageMode = (stores, scrollTop) => {
  const totPages = getTotalPages(stores);
  let index;
  for (index = 0; index < totPages; index++) {
    scrollTop -= Math.max(getPageHeight(stores, index), getViewPortHeight(stores));
    if (scrollTop < 0) {
      break;
    }
  }
  return index;
};
